import { Link } from 'react-router-dom'
import Breadcrumb from '@/components/ui/Breadcrumb'
import Pagination from '@/components/ui/Pagination'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useJobs, useSearchJobs } from '@/lib/api/hooks/job'
import type { Job } from '@/lib/api/types'

import Search from '@/components/ui/Search'

import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay'

import { useSearchFilterParams } from '@/hooks/useSearchFilterParams'

import FILTER_SECTIONS from '@/components/pages/Opportunities/Careers/FilterModule/FILTER_SECTIONS'

import FilterModule from '@/components/ui/FilterModule'


interface JobItem {
  id: string
  title: string
  location: string
  type: string
  organization: string
  postingDate: string
  endDate: string
  status: 'Open' | 'Closed'
  href: string
}

// Helper function to format date from "2025-11-15" to "15th Nov. 2025"
const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  const day = date.getDate()
  const monthNames = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.']
  const month = monthNames[date.getMonth()]
  const year = date.getFullYear()
  
  // Add ordinal suffix
  const ordinal = (n: number) => {
    const s = ['th', 'st', 'nd', 'rd']
    const v = n % 100
    return n + (s[(v - 20) % 10] || s[v] || s[0])
  }
  
  return `${ordinal(day)} ${month} ${year}`
}

// Helper function to determine job status based on endDate
const getJobStatus = (endDate: string): 'Open' | 'Closed' => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const end = new Date(endDate)
  end.setHours(0, 0, 0, 0)
  return end >= today ? 'Open' : 'Closed'
}

// Helper function to format job type
const formatJobType = (jobType: string): string => {
  const typeMap: Record<string, string> = {
    'FULL_TIME': 'Fulltime',
    'PER_TIME': 'Part-time',
    'PART_TIME': 'Part-time',
    'CONTRACT': 'Contract',
    'INTERNSHIP': 'Internship'
  }
  return typeMap[jobType] || jobType
}

const Jobs = () => {

  // ── URL-driven search / filter state ──────────────────────────────────────
    const {
      mode,
      searchTerm,
      filterState,
      page,
      size,
      setSearch,
      setFilters,
      removeFilter,
      clearFilters,
      setPage,
      apiFilters,
      getActiveFilterCount
    } = useSearchFilterParams(FILTER_SECTIONS)
  


  // Search API (when searching)
  const { data: searchData, isLoading: isSearchLoading, isError: isSearchError, refetch: refetchSearch } = useSearchJobs(
    searchTerm,
    { page, size }
  )

  // Filter API (when filtering or default)
  const { data: filterData, isLoading: isFilterLoading, isError: isFilterError, refetch: refetchFilter } = useJobs(
    { page, size },
    apiFilters
  )

  // Select appropriate data source: search takes priority
  const jobsResponse = mode === 'search' ? searchData : filterData
  const isLoading = mode === 'search' ? isSearchLoading : isFilterLoading
  const isError = mode === 'search' ? isSearchError : isFilterError

  const totalPages = jobsResponse?.data?.totalPage ?? 1
  const activeFilterCount = getActiveFilterCount(FILTER_SECTIONS)
  const refetchData = mode === 'search' ? refetchSearch : refetchFilter
  const emptyMessage = mode === 'search'
    ? 'No jobs found matching your search criteria.'
    : mode === "filter" ?  'No jobs found matching your filter criteria.'
    : 'No jobs found.'

    const jobs: JobItem[] = Array.isArray(jobsResponse?.data?.data) ? jobsResponse.data.data.map((job: Job) => {
      const status = getJobStatus(job.endDate)
      return {
        id: job.jobId,
        title: job.jobTitle,
        location: job.jobLocation,
        type: formatJobType(job.jobType),
        organization: 'The African Union Commission', // Not in API, using default
        postingDate: job.datePublished ? formatDate(job.datePublished) : formatDate(job.startDate),
        endDate: formatDate(job.endDate),
        status,
        href: `/opportunities/careers/${job.jobId}`
      }
    }) || [] : []

  // Transform backend jobs to component format
  // const jobs: JobItem[] = useMemo(() => {
  //   const backendJobs = jobsResponse?.data?.data || []
    
  //   let transformedJobs = backendJobs.map((job: Job) => {
  //     const status = getJobStatus(job.endDate)
  //     return {
  //       id: job.jobId,
  //       title: job.jobTitle,
  //       location: job.jobLocation,
  //       type: formatJobType(job.jobType),
  //       organization: 'The African Union Commission', // Not in API, using default
  //       postingDate: job.datePublished ? formatDate(job.datePublished) : formatDate(job.startDate),
  //       endDate: formatDate(job.endDate),
  //       status,
  //       href: `/opportunities/careers/${job.jobId}`
  //     }
  //   })

  //   // Apply search filter
  //   if (searchQuery) {
  //     transformedJobs = transformedJobs.filter(job =>
  //       job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //       job.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //       job.type.toLowerCase().includes(searchQuery.toLowerCase())
  //     )
  //   }

  //   // Apply status filter (client-side since API doesn't support it)
  //   if (filters.status && filters.status.length > 0 && !filters.status.includes('All Status')) {
  //     transformedJobs = transformedJobs.filter(job =>
  //       filters.status.includes(job.status)
  //     )
  //   }

  //   return transformedJobs
  // }, [jobsResponse, searchQuery, filters.status])

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Careers', href: '/opportunities/careers' },
    { label: 'Jobs', href: '/opportunities/careers/jobs', active: true }
  ]

  return (
    <div className="bg-white min-h-screen">
      {/* Breadcrumb */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8  ">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 pt-6 sm:px-6 lg:px-8 pb-16">

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 gap-6">
            {/* Search + Filter Controls */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center relative gap-4 justify-between flex-wrap">
              <Search
                value={searchTerm}
                onSearch={setSearch}
                placeholder="Search jobs..."
              />
               <FilterModule
                    sections={FILTER_SECTIONS}
                    activeFilters={filterState}
                    onApply={setFilters}
                    activeFilterCount={activeFilterCount}
                  />
            </div>

            {/* Active filter chips — only shown in filter mode */}
            <FilterParamsDisplay
              sections={FILTER_SECTIONS}
              filterState={filterState}
              onRemove={removeFilter}
              onClearAll={clearFilters}
            />
          </div>

        {/* Right Content - Job Listings */}
          <div className="space-y-4">
            {isLoading ? (
              <LoadingSpinner message="Loading jobs..." className="py-12" />
            ) : isError ? <div className="text-center flex flex-col items-center justify-center py-12">
                <p className="text-lg text-gray-500">An error occurred while fetching jobs.</p>
                <button
                  onClick={() => refetchData()}
                  className="mt-4 px-4 py-2 bg-wine-100 text-white rounded hover:bg-wine-200 transition-colors"
                >
                  Retry
                </button>
              </div> : jobs.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-lg">{emptyMessage}</p>
              </div>
            ) : (
              <>
                {/* Job Cards */}
                {jobs.map((job) => (
                  <Link
                    key={job.id}
                    to={job.href}
                    className="block bg-white border border-gray-200 rounded-lg p-6 hover:bg-gray-50 transition-colors"
                  >
                <div className="flex justify-between items-start gap-6">
                  <div className="flex-1 space-y-3">
                    <h3 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900">
                      {job.title}
                    </h3>
                    
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-wine-100 font-medium">Location:</span>
                      <span className="text-gray-900">{job.location}</span>
                      <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                      
                      <span className="text-wine-100 font-medium">Type:</span>
                      <span className="text-gray-900">{job.type}</span>
                      <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                      
                      <span className="text-wine-100 font-medium">Organization:</span>
                      <span className="text-gray-900">{job.organization}</span>
                      <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                      
                      <span className="text-wine-100 font-medium">Posting date:</span>
                      <span className="text-gray-900">{job.postingDate}</span>
                      <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                      
                      <span className="text-wine-100 font-medium">End date:</span>
                      <span className="text-gray-900">{job.endDate}</span>
                    </div>
                  </div>
                  
                  <div className={`px-3 py-1 rounded-md flex items-center gap-1.5 ${
                    job.status === 'Open' 
                      ? 'bg-emerald-50' 
                      : 'bg-wine-100/10'
                  }`}>
                    <div className={`w-2 h-2 rounded-full ${
                      job.status === 'Open'
                        ? 'bg-forestGreen-100'
                        : 'bg-wine-100'
                    }`}></div>
                    <span className={`text-sm font-medium font-satoshi ${
                      job.status === 'Open'
                        ? 'text-forestGreen-100'
                        : 'text-wine-100'
                    }`}>
                      {job.status}
                    </span>
                  </div>
                </div>
              </Link>
                ))}

                <div className="mt-8">
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                      setPage(page)
                      // Scroll to top when page changes
                      window.scrollTo({ top: 0, behavior: 'smooth' })
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Jobs